// eslint-disable-next-line max-classes-per-file
import {
  DtoKeys, Enums, Helpers, Mappers,
} from '@bode-canada/core';
import type { UserListingDto } from '@/types/user';
import type { ListingFlowDto } from '@/types/listing-flow';
import type { FullListingDto, ShowingDays } from '@/types/new-listing';
import type {
  OldFullListingDto, OldListingDto, OldShowingDays, OldUserListingDto,
} from '@/types/old-listing';
import NewListingApiService from '../../../../services/api/serverless/new-listing';

const ALLOWED_USER_ROLES = [Enums.UserListingRoleEnum.OWNER, Enums.UserListingRoleEnum.ATH];

class UserListingMapper {
  static mapToOldDto(userListing: UserListingDto): OldUserListingDto {
    return {
      role: userListing[DtoKeys.UserDtoKey.ROLE],
      email: userListing[DtoKeys.UserDtoKey.EMAIL],
      phone: userListing[DtoKeys.UserDtoKey.PHONE],
      userId: userListing[DtoKeys.UserDtoKey.USER_ID],
      lastName: userListing[DtoKeys.UserDtoKey.LAST_NAME],
      nickName: userListing[DtoKeys.UserDtoKey.NICK_NAME],
      firstName: userListing[DtoKeys.UserDtoKey.FIRST_NAME],
      profilePhoto: userListing[DtoKeys.UserDtoKey.PROFILE_IMAGE_URL],
      idVerificationStatus: userListing[DtoKeys.UserDtoKey.STATUS],
      userBusinessAccounts: userListing[DtoKeys.UserDtoKey.USER_BUSINESS_ACCOUNTS],
    };
  }
}

export class ListingMapper {
  static mapToOldDto(listingFlow: FullListingDto | ListingFlowDto): OldFullListingDto {
    return {
      listing: this.mapListingToOldListing(listingFlow),
      users: ('users' in listingFlow ? listingFlow.users : [])
        .filter((user) => ALLOWED_USER_ROLES.includes(user.role))
        .map((user) => UserListingMapper.mapToOldDto(user)),
    };
  }

  // TODO: create Zoho interface
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static mapToNewDto(listing: OldListingDto) {
    return {
      listing: {
        id: listing.listingId,
      },
      address: {
        city: listing.city,
        state: listing.province,
        country: listing.country,
        streetNumber: listing.streetNumber,
        unit: listing.unit || '',
        street: listing.street,
      },
    };
  }

  private static mapListingToOldListing(
    listingFlow: FullListingDto | ListingFlowDto,
  ): OldListingDto {
    const listing = listingFlow[DtoKeys.ListingFlowDtoKey.LISTING];
    const address = listingFlow[DtoKeys.ListingFlowDtoKey.ADDRESS];
    const property = listingFlow[DtoKeys.ListingFlowDtoKey.PROPERTY];
    const user = 'user' in listingFlow ? listingFlow[DtoKeys.ListingFlowDtoKey.USER] : null;

    return {
      isBuilder:
        listing[DtoKeys.NewListingDtoKey.OWNERSHIP_TYPE] === Enums.OwnershipTypeEnum.A_COMPANY,
      phoneNumber: listing[DtoKeys.NewListingDtoKey.PHONE],
      hasConsentDisplayPhoneNumberOnListing: listing[DtoKeys.NewListingDtoKey.SHOW_PHONE],
      isOnlyOwnerVisible: (listing[DtoKeys.NewListingDtoKey.OWNERSHIP_DISPLAY]
        === Enums.OwnershipDisplayEnum.USE_MY_NAME_ONLY),
      origin: listing[DtoKeys.NewListingDtoKey.ORIGIN],
      address: Helpers.StringHelper.joinStrings([
        address[DtoKeys.AddressDtoKey.STREET_NUMBER],
        address[DtoKeys.AddressDtoKey.STREET],
      ], ' '),
      streetNumber: address[DtoKeys.AddressDtoKey.STREET_NUMBER],
      street: address[DtoKeys.AddressDtoKey.STREET],
      unit: address[DtoKeys.AddressDtoKey.UNIT],
      city: address[DtoKeys.AddressDtoKey.CITY],
      country: address[DtoKeys.AddressDtoKey.COUNTRY],
      community: address[DtoKeys.AddressDtoKey.COMMUNITY],
      province: address[DtoKeys.AddressDtoKey.STATE],
      postalCode: address[DtoKeys.AddressDtoKey.POSTAL_CODE],
      addressId: address[DtoKeys.AddressDtoKey.ID],
      appliancesIncluded: listing[DtoKeys.NewListingDtoKey.APPLIANCES_INCLUDED] || [],
      availabilities: this
        .mapShowingDaysIntoAvailabilities(listing[DtoKeys.NewListingDtoKey.SHOWING_DAYS] || []),
      availableUnits: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.TOTAL_NUMBER_OF_LEGAL_UNITS],
      multiUnit: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.IS_MULTI_UNIT],
      balanceSalePriceCommission: listing[DtoKeys.NewListingDtoKey.COMMISSION]
        ?.[DtoKeys.NewListingCommissionDtoKey.BALANCE_OF_SALE_PRICE_COMMISSION],
      basementDevFeatures: property[DtoKeys.PropertyDtoKey.INTERIOR]
        ?.[DtoKeys.PropertyInteriorDtoKey.BASEMENT_DETAILS],
      basementFeatures: property[DtoKeys.PropertyDtoKey.INTERIOR]
        ?.[DtoKeys.PropertyInteriorDtoKey.BASEMENT_FEATURES],
      beds: property[DtoKeys.PropertyDtoKey.BEDS],
      fullBaths: property[DtoKeys.PropertyDtoKey.FULL_BATHS],
      halfBaths: property[DtoKeys.PropertyDtoKey.HALF_BATHS],
      halfBeds: property[DtoKeys.PropertyDtoKey.HALF_BEDS],
      buildingClass: property[DtoKeys.PropertyDtoKey.BUILDING_CLASS],
      buildingType: property[DtoKeys.PropertyDtoKey.BUILDING_TYPE],
      commissionType: listing[DtoKeys.NewListingDtoKey.COMMISSION]
        ?.[DtoKeys.NewListingCommissionDtoKey.COMMISSION_TYPE],
      communityFeatures: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.COMMUNITY_FEATURES],
      condoFee: property[DtoKeys.PropertyDtoKey.CONDO_INFORMATION]
        ?.[DtoKeys.CondoInformationDtoKey.CONDO_FEE],
      condoFeeIncludes: property[DtoKeys.PropertyDtoKey.CONDO_INFORMATION]
        ?.[DtoKeys.CondoInformationDtoKey.CONDO_FEE_INCLUDES],
      condoFeePaymentSchedule: property[DtoKeys.PropertyDtoKey.CONDO_INFORMATION]
        ?.[DtoKeys.CondoInformationDtoKey.CONDO_FEE_PAYMENT_SCHEDULE],
      condoType: property[DtoKeys.PropertyDtoKey.CONDO_INFORMATION]
        ?.[DtoKeys.CondoInformationDtoKey.CONDO_TYPE],
      constructionType: property[DtoKeys.PropertyDtoKey.CONSTRUCTION_TYPE],
      cooling: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.COOLING],
      externalId: listing[DtoKeys.NewListingDtoKey.MLS_NUMBER],
      signers: listing[DtoKeys.NewListingDtoKey.ENVELOPE]?.recipients?.signers || [],
      haveAllSellersCompletedListingAgreement: true,
      user,
      createdAt: null,
      closedAt: listing[DtoKeys.NewListingDtoKey.CLOSED_AT],
      listPrice: listing[DtoKeys.NewListingDtoKey.LIST_PRICE],
      daysOnMarket: listing[DtoKeys.NewListingDtoKey.DAYS_ON_MARKET],
      commissionSavings: listing[DtoKeys.NewListingDtoKey.COMMISSION_SAVINGS],
      envelopeId: null,
      hasBuySideCommission: null,
      landUse: property[DtoKeys.PropertyDtoKey.LAND_USE],
      legalSuite: null,
      lot: null,
      plan: null,
      priceUpdatedAt: null,
      publicRemarks: null,
      publishedAt: null,
      updatedAt: listing[DtoKeys.NewListingDtoKey.UPDATED_AT],
      videoTour: listing[DtoKeys.NewListingDtoKey.VIDEO_TOUR],
      virtualTour: listing[DtoKeys.NewListingDtoKey.VIRTUAL_TOUR],
      description: listing[DtoKeys.NewListingDtoKey.DESCRIPTION],
      directions: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.DIRECTIONS],
      doorCode: listing[DtoKeys.NewListingDtoKey.DOOR_CODE],
      elevator: property[DtoKeys.PropertyDtoKey.APARTMENT_DETAILS]
        ?.[DtoKeys.ApartmentDetailsDtoKey.IS_THERE_AN_ELEVATOR_IN_BUILDING],
      elevatorsAmount: property[DtoKeys.PropertyDtoKey.APARTMENT_DETAILS]
        ?.[DtoKeys.ApartmentDetailsDtoKey.NUMBER_OF_ELEVATORS],
      exterior: property[DtoKeys.PropertyDtoKey.EXTERIOR]
        ?.[DtoKeys.PropertyExteriorDtoKey.EXTERIOR_MATERIAL],
      exteriorFeatures: property[DtoKeys.PropertyDtoKey.EXTERIOR]
        ?.[DtoKeys.PropertyExteriorDtoKey.EXTERIOR_FEATURES],
      lotFeatures: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.LOT_FEATURES],
      finishedLevels: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.FINISHED_LEVELS],
      finishedSqft: property[DtoKeys.PropertyDtoKey.TOTAL_FINISH_SQFT],
      fireplacesAmount: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.FIREPLACES_AMOUNT],
      fireplacesFuel: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.FIREPLACES_FUEL],
      firstPartOfSalePriceCommission: listing[DtoKeys.NewListingDtoKey.COMMISSION]
        ?.[DtoKeys.NewListingCommissionDtoKey.FIRST_PART_OF_SALE_PRICE_COMMISSION],
      flatRate: listing[DtoKeys.NewListingDtoKey.COMMISSION]
        ?.[DtoKeys.NewListingCommissionDtoKey.FLAT_RATE],
      floorLocation: property[DtoKeys.PropertyDtoKey.APARTMENT_DETAILS]
        ?.[DtoKeys.ApartmentDetailsDtoKey.FLOOR_LOCATION],
      flooring: property[DtoKeys.PropertyDtoKey.INTERIOR]
        ?.[DtoKeys.PropertyInteriorDtoKey.FLOORING],
      foundation: property[DtoKeys.PropertyDtoKey.FOUNDATION_TYPE],
      frontExposure: property[DtoKeys.PropertyDtoKey.EXTERIOR]
        ?.[DtoKeys.PropertyExteriorDtoKey.FRONT_EXPOSURE],
      goodsIncluded: listing[DtoKeys.NewListingDtoKey.OTHER_GOODS_INCLUDED],
      garageSqft: property[DtoKeys.PropertyDtoKey.PARKING_DETAILS]
        ?.[DtoKeys.ParkingDetailsDtoKey.TOTAL_GARAGE_SQFT],
      parkingFeatures: property[DtoKeys.PropertyDtoKey.PARKING_DETAILS]
        ?.[DtoKeys.ParkingDetailsDtoKey.PARKING_FEATURES],
      parkingSpace: property[DtoKeys.PropertyDtoKey.PARKING_DETAILS]
        ?.[DtoKeys.ParkingDetailsDtoKey.PARKING_SPACE_INCLUDED],
      totalParking: property[DtoKeys.PropertyDtoKey.PARKING_DETAILS]
        ?.[DtoKeys.ParkingDetailsDtoKey.TOTAL_PARKING_SPACES],
      heatingFuel: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.HEATING_FUEL],
      heatingTypes: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.HEATING_TYPES],
      hoaFeeAmount: property[DtoKeys.PropertyDtoKey.HOMEOWNER_ASSOCIATION]
        ?.[DtoKeys.HoaDtoKey.HOA_FEE_AMOUNT],
      hoaFeeIncludes: property[DtoKeys.PropertyDtoKey.HOMEOWNER_ASSOCIATION]
        ?.[DtoKeys.HoaDtoKey.HOA_FEE_INCLUDES],
      hoaFeeRequired: property[DtoKeys.PropertyDtoKey.HOMEOWNER_ASSOCIATION]
        ?.[DtoKeys.HoaDtoKey.HOA_FEE_APPLIES],
      hoaFeeSchedule: property[DtoKeys.PropertyDtoKey.HOMEOWNER_ASSOCIATION]
        ?.[DtoKeys.HoaDtoKey.HOA_FEE_SCHEDULE],
      images: listing[DtoKeys.NewListingDtoKey.IMAGES],
      interiorFeatures: property[DtoKeys.PropertyDtoKey.INTERIOR]
        ?.[DtoKeys.PropertyInteriorDtoKey.INTERIOR_FEATURES],
      isPetFriendly: !!property[DtoKeys.PropertyDtoKey.CONDO_INFORMATION]
        ?.[DtoKeys.CondoInformationDtoKey.IS_CONDO_PET_FRIENDLY],
      isCondo: property[DtoKeys.PropertyDtoKey.CONDO_INFORMATION]
        ?.[DtoKeys.CondoInformationDtoKey.IS_CONDO],
      listingId: listing[DtoKeys.NewListingDtoKey.ID],
      location: [
        Number(address[DtoKeys.AddressDtoKey.LONGITUDE]),
        Number(address[DtoKeys.AddressDtoKey.LATITUDE]),
      ],
      lotSizeSqft: property[DtoKeys.PropertyDtoKey.LOT_SIZE_SQFT],
      occupancy: listing[DtoKeys.NewListingDtoKey.OCCUPANCY],
      otherEquipmentIncluded: listing[DtoKeys.NewListingDtoKey.OTHER_GOODS_INCLUDED],
      ownersHighlight: listing[DtoKeys.NewListingDtoKey.OWNERS_HIGHLIGHTS],
      ownershipType: listing[DtoKeys.NewListingDtoKey.OWNERSHIP_TYPE],
      petRestrictions: property[DtoKeys.PropertyDtoKey.CONDO_INFORMATION]
        ?.[DtoKeys.CondoInformationDtoKey.PET_RESTRICTIONS],
      possession: listing[DtoKeys.NewListingDtoKey.PREFERRED_POSSESSION_TIMING],
      possessionDate: listing[DtoKeys.NewListingDtoKey.PREFERRED_POSSESSION_TIMING_DATE],
      postTensionCable: property[DtoKeys.PropertyDtoKey.APARTMENT_DETAILS]
        ?.[DtoKeys.ApartmentDetailsDtoKey.POST_TENSION_CABLE_CONSTRUCTION],
      price: listing[DtoKeys.NewListingDtoKey.PRICE],
      requiresIdVerificationForShowings:
        listing[DtoKeys.NewListingDtoKey.IS_ID_VERIFICATION_REQUIRED],
      roadAccess: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.ROAD_ACCESS],
      roofType: property[DtoKeys.PropertyDtoKey.EXTERIOR]
        ?.[DtoKeys.PropertyExteriorDtoKey.ROOF_TYPE],
      septic: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.SEWER_SEPTIC],
      sewer: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.SEWER_SEPTIC],
      signSelection: listing[DtoKeys.NewListingDtoKey.SIGN_SELECTED],
      siteServices: property[DtoKeys.PropertyDtoKey.PROPERTY_DETAILS]
        ?.[DtoKeys.PropertyDetailsDtoKey.SITE_SERVICES],
      status: listing[DtoKeys.NewListingDtoKey.STATUS],
      storageType: property[DtoKeys.PropertyDtoKey.APARTMENT_DETAILS]
        ?.[DtoKeys.ApartmentDetailsDtoKey.STORAGE_TYPE],
      storageUnitNumber: property[DtoKeys.PropertyDtoKey.APARTMENT_DETAILS]
        ?.[DtoKeys.ApartmentDetailsDtoKey.STORAGE_UNIT_NUMBER],
      style: property[DtoKeys.PropertyDtoKey.STYLE],
      suiteLaundry: property[DtoKeys.PropertyDtoKey.APARTMENT_DETAILS]
        ?.[DtoKeys.ApartmentDetailsDtoKey.IN_SUITE_LAUNDRY_INCLUDED],
      waterSupply: property[DtoKeys.PropertyDtoKey.MECHANICAL_DETAILS]
        ?.[DtoKeys.MechanicalDetailsDtoKey.WATER_SUPPLY],
      yearBuilt: property[DtoKeys.PropertyDtoKey.YEAR_BUILT],
      taxes: property[DtoKeys.PropertyDtoKey.TAXES],
    };
  }

  private static mapShowingDaysIntoAvailabilities(showingDays: ShowingDays[]): OldShowingDays[] {
    return [showingDays.reduce((acc, showingDay) => ({
      ...acc,
      startMinutes: acc.startMinutes || showingDay.from,
      endMinutes: acc.endMinutes || showingDay.to,
      weekdays: showingDay.day
        ? [...acc.weekdays, Mappers.DateMapper.mapDayOfWeekToShort(showingDay.day)]
        : acc.weekdays,
    }), { startMinutes: undefined, endMinutes: undefined, weekdays: [] } as OldShowingDays)];
  }
}

export default class LoadListingService {
  static async loadListing(listingId: string): Promise<OldFullListingDto | null> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const draftListing: FullListingDto = await NewListingApiService.getById(listingId);

      return ListingMapper.mapToOldDto(draftListing);
    } catch {
      return null;
    }
  }
}
